import React from 'react';
import { PaginatedResponse } from '../../types/pagination';

interface PaginationControlsProps {
  pagination: PaginatedResponse<unknown>['pagination'];
  itemLabel: string;
  onPageChange: (page: number) => void;
}

export const PaginationControls: React.FC<PaginationControlsProps> = ({
  pagination,
  itemLabel,
  onPageChange,
}) => {
  const {
    page,
    pageSize,
    totalItems,
    totalPages,
    hasNextPage,
    hasPreviousPage,
  } = pagination;

  if (totalItems === 0) {
    return null;
  }

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalItems);

  const pageNumbers = Array.from(
    { length: totalPages },
    (_, index) => index + 1,
  )
    .filter((value) => {
      if (totalPages <= 7) return true;
      return value === 1 || value === totalPages || Math.abs(value - page) <= 1;
    })
    .reduce<Array<number | 'ellipsis'>>((acc, value) => {
      const last = acc[acc.length - 1];

      if (typeof last === 'number' && value - last > 1) {
        acc.push('ellipsis');
      }

      acc.push(value);
      return acc;
    }, []);

  return (
    <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3">
      <p className="text-xs text-gray-400">
        Showing {startItem}-{endItem} of {totalItems} {itemLabel}
      </p>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={!hasPreviousPage}
          onClick={() => onPageChange(page - 1)}
          className="flex h-7 items-center gap-1 rounded-lg border border-gray-200 px-2.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
        >
          Previous
        </button>
        {pageNumbers.map((value) =>
          value === 'ellipsis' ? (
            <span
              key={`ellipsis-${value}`}
              className="px-1 text-xs text-gray-400"
            >
              ...
            </span>
          ) : (
            <button
              key={value}
              type="button"
              onClick={() => onPageChange(value)}
              className={[
                'flex h-7 w-7 items-center justify-center rounded-lg border text-xs',
                page === value
                  ? 'border-pos-primary bg-pos-primary font-semibold text-white'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50',
              ].join(' ')}
            >
              {value}
            </button>
          ),
        )}
        <button
          type="button"
          disabled={!hasNextPage}
          onClick={() => onPageChange(page + 1)}
          className="flex h-7 items-center gap-1 rounded-lg border border-gray-200 px-2.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
};
